import React, { useState, useEffect, useMemo } from "react";
import StatsBox from "../StatsPage/StatsBox.js";
import InfoTable from "../InfoTable.js";
import ResultInfoText from "../ResultsTable/ResultInfoText.js";
import KingdomIcon from "../ReactionTable/kingdomIcon.js";
import MoleculesDiv from "../MoleculeTable/MoleculesDiv.js";
import Tooltip from "../Tooltip.js";
import getDataByEnzymesFromSearchResults from "../../api/transformSearchResultsToEnzymes.js";
import EnzymeBasicInfo from "./EnzymeBasicInfo.js";
import AminoAcidDisplay from "./AminoAcidDisplay.js";
import ReactionResultBox from "../ResultsTable/ReactionResultBox.js";
import SmallLoading from "../smallLoading.js";
import MechanismViewer from "../MechanismView/mechanismViewer.js";
import StructureBlock from "../Structure/StructureBlock.js";
import api from "../../api/axios.js";



function getRegexIntervals(str, regex) {
  if (!str || !(regex instanceof RegExp)) return [];
  let matches = [];
  let match;
  let globalRegex = new RegExp(regex, regex.flags.includes("g") ? regex.flags : regex.flags + "g");
  while ((match = globalRegex.exec(str)) !== null) {
    matches.push([match.index, match.index + match[0].length]);
  }
  return matches;
}

function EnzymeRecord({ searchResult, setMechBody, navigateToRecord }) {
  const [ddxxd, setDdxxd] = useState([]);
  const [dxdd, setDxdd] = useState([]);
  const [nsedte, setNsedte] = useState([]);
  const [nsedteExp, setNsedteExp] = useState([]);
  const [dbMotifs, setDbMotifs] = useState([]);


  const enzymeData = useMemo(() => {
    if (!searchResult?.marts) return null;
    if (!searchResult?.result) return null;
    const list = getDataByEnzymesFromSearchResults(searchResult.result);
    return list?.[0] ?? null;
  }, [searchResult?.result]);

  useEffect(() => {
    if (!enzymeData?.marts) { setDbMotifs([]); return; }
    let cancel = false;
    api.get("/motifs/by-enzyme", { params: { marts: enzymeData.marts } })
      .then(resp => { if (!cancel) setDbMotifs(resp.data?.motifs || []); })
      .catch(() => { if (!cancel) setDbMotifs([]); });
    return () => { cancel = true; };
  }, [enzymeData?.marts]);

  // Fallback
  useEffect(() => {
    if (!enzymeData?.aminoacid_sequence) return;
    const sequence = enzymeData.aminoacid_sequence;
    setDdxxd(getRegexIntervals(sequence, /DD..[DE]/g));
    setDxdd(getRegexIntervals(sequence, /D.DD/g));
    setNsedte(getRegexIntervals(sequence, /[DN]D[LIV].[ST]...E/g));
    setNsedteExp(getRegexIntervals(sequence, /[DN]D..[STG]...E/g));
  }, [enzymeData]);

  const pickColor = (i) => {
    const palette = ["#ffd700", "#ff8c00", "#dc143c", "#2e8b57", "#1e90ff", "#8a2be2", "#ff1493"];
    return palette[i % palette.length];
  };

  const motifs = useMemo(() => {
    if (dbMotifs?.length > 0) {
      const byName = new Map();
      for (const r of dbMotifs) {
        const name = r.name || `Motif ${r.motif_id}`;
        const arr = byName.get(name) || [];
        const start0 = Math.max(0, (r.start_pos ?? 1) - 1);
        const end0 = r.end_pos ?? start0;
        arr.push([start0, end0]);
        byName.set(name, arr);
      }
      let i = 0;
      return Array.from(byName.entries()).map(([name, ranges]) => ({
        key: name.toLowerCase().replace(/\s+/g, "_"),
        name,
        ranges,
        color: pickColor(i++)
      })).filter(m => m.ranges.length > 0);
    }
    // Fallback
    return [
      { key: 'ddxxd',     name: 'DDXXD',            ranges: ddxxd,     color: 'yellow' },
      { key: 'dxdd',      name: 'DXDD',             ranges: dxdd,      color: 'orange' },
      { key: 'nsedte',    name: 'NSE/DTE',          ranges: nsedte,    color: 'red' },
      { key: 'nsedteExp', name: 'NSE/DTE expanded', ranges: nsedteExp, color: 'green' },
    ].filter(m => m.ranges?.length > 0);
  }, [dbMotifs, ddxxd, dxdd, nsedte, nsedteExp]);

  if (!searchResult.marts) {
    return <div className="loading-div"><SmallLoading /></div>;
  }
    const history = searchResult.marts;
    if (searchResult.marts && !searchResult.result){

        return (
          <>
        <StatsBox title="Deleted record">
          This record has been deleted from MARTS. Look at the history below for more information.
          </StatsBox>
            <StatsBox title="Record history">
            <InfoTable data={[["Date"].concat(history.map(row => row.note_date.split("T")[0])), ["Record"].concat(history.map(row => row.note))]} />
        </StatsBox></>
        );
    }  if (!enzymeData) {
    return null;
  }

  const hasMechanism = enzymeData.reactions.some(r => !!r.mechanism_marts);
  

  return (
        <>
        <StatsBox title={
            <><ResultInfoText text={enzymeData.name} row={1} fontSize="25px" buttonClass="plane-text-button-no-background"/>  {" · "}
            <ResultInfoText text={enzymeData.marts} row={1} fontSize="20px" buttonClass="plane-text-button-no-background"/></>}>
            <EnzymeBasicInfo enzymeData={enzymeData} />
        </StatsBox>
        <StatsBox title="Reactions">
        {enzymeData.reactions.map((reaction, index) => (
            <ReactionResultBox table={enzymeData.reactions} row={index} interactiveSearch={() => {}} setMechBody={setMechBody} maxWidth="150px" imageScale="1" displayTextCol={true} navigateToRecord={navigateToRecord}/>
            ))}
        </StatsBox>
        {hasMechanism && (
            <StatsBox title="Mechanism">
                <MechanismViewer searchData={{enzyme_marts: [enzymeData.marts]}}  orientation={ enzymeData.reactions.length > 10 ? "TD" : "LR" }/>
            </StatsBox>
        )}
        {
            (searchResult.negatives && searchResult.negatives.length > 0) && (
        <StatsBox title={<Tooltip content={`These substrates were reported as inactive for this enzyme.`}>Inactive substrates</Tooltip>}>
                <MoleculesDiv molecules={searchResult.negatives} startingDisplay={18} displayStep={18} />
        </StatsBox>
            )
        }
        <StatsBox title="Amino acid sequence">
            <AminoAcidDisplay sequence={enzymeData.aminoacid_sequence} enzymeInfo={enzymeData} ddxxd={ddxxd} dxdd={dxdd} nsedte={nsedte} nsedteExp={nsedteExp} />
        </StatsBox> 

        <StructureBlock
        enzymeMarts={enzymeData.marts}
        motifs={motifs}
        navigateToRecord={navigateToRecord}
      />
           
        <StatsBox title="Record history">
            <InfoTable data={[["Date"].concat(history.map(row => row.note_date.split("T")[0])), ["Record"].concat(history.map(row => row.note))]} />
        </StatsBox>
        </ >
    );
}

export default EnzymeRecord;